/**
 * ConfigurationRow Component
 *
 * Renders a single row in the configurations table.
 * Displays configuration details with toggle, environment badges, and action buttons.
 */

var React = require('react');
var {
  formatEnvironments,
  formatRelativeTime,
} = require('./RemoteConfigurations.util');

/**
 * Format targeting rules for display (extract name from "name :True" format)
 * @param {Array} items - Array of rule strings
 * @returns {Array} Formatted items with name and value
 */
var formatTargetingRules = function (items) {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return [];
  }

  return items
    .map(function (item) {
      var parts = item.split(':');
      var name = parts[0].trim();
      var value = parts[1] ? parts[1].trim().toLowerCase() === 'true' : true;
      return { name: name, value: value };
    })
    .sort(function (a, b) {
      return a.name.localeCompare(b.name);
    });
};

/**
 * ConfigurationRow component
 * @param {Object} props
 * @param {Object} props.configuration - Configuration data
 * @param {boolean} props.isSelected - Whether this row is selected
 * @param {Function} props.onSelect - Called when row is clicked
 * @param {Function} props.onToggle - Called when toggle is clicked
 * @param {Function} props.onEdit - Called when edit button is clicked
 * @param {Function} props.onDelete - Called when delete button is clicked
 */
var ConfigurationRow = function (props) {
  var configuration = props.configuration;
  var isSelected = props.isSelected;
  var onSelect = props.onSelect;
  var onToggle = props.onToggle;
  var onEdit = props.onEdit;
  var onDelete = props.onDelete;

  var isEnabled =
    configuration.default_value === true ||
    configuration.default_value === 'true';
  var environments = formatEnvironments(configuration.environments);
  var distributions = formatTargetingRules(configuration.distributions);
  var farmingUnits = formatTargetingRules(configuration.farming_units);
  var users = (configuration.users_email || []).slice().sort(function (a, b) {
    return a.localeCompare(b);
  });
  var updatedTime = formatRelativeTime(configuration.updated_at);

  var handleRowClick = function (e) {
    // Don't select when clicking on toggle or action buttons
    if (
      e.target.closest('.remote-config-toggle') ||
      e.target.closest('.remote-config-actions')
    ) {
      return;
    }
    if (onSelect) {
      onSelect(configuration);
    }
  };

  var handleToggleClick = function (e) {
    e.stopPropagation();
    e.preventDefault();
    if (onToggle) {
      onToggle(configuration);
    }
  };

  var handleEditClick = function (e) {
    e.stopPropagation();
    if (onEdit) {
      onEdit(configuration);
    }
  };

  var handleDeleteClick = function (e) {
    e.stopPropagation();
    if (onDelete) {
      onDelete(configuration);
    }
  };

  var rowClassName = 'remote-config-table__row';
  if (isSelected) {
    rowClassName += ' remote-config-table__row--selected';
  }

  // Render a targeting rule badge
  var renderRuleBadge = function (rule, index, prefix) {
    var badgeClass = rule.value
      ? 'remote-config-rule-badge--on'
      : 'remote-config-rule-badge--off';
    return React.createElement(
      'span',
      {
        key: prefix + '-' + index,
        className: 'remote-config-rule-badge ' + badgeClass,
        title: rule.name + ': ' + (rule.value ? 'True' : 'False'),
      },
      rule.name,
    );
  };

  // Check if there are any targeting rules
  var hasTargeting =
    distributions.length > 0 || farmingUnits.length > 0 || users.length > 0;

  return React.createElement(
    'tr',
    {
      className: rowClassName,
      onClick: handleRowClick,
    },
    [
      // Name column
      React.createElement(
        'td',
        {
          key: 'name',
          className: 'remote-config-table__td',
        },
        [
          React.createElement(
            'div',
            {
              key: 'name-text',
              className: 'remote-config-name',
            },
            configuration.configuration_name,
          ),
          configuration.description &&
            React.createElement(
              'div',
              {
                key: 'description',
                className: 'remote-config-description',
                title: configuration.description,
              },
              configuration.description,
            ),
        ],
      ),

      // Status toggle column
      React.createElement(
        'td',
        {
          key: 'status',
          className: 'remote-config-table__td',
        },
        React.createElement(
          'label',
          {
            className: 'remote-config-toggle',
            onClick: handleToggleClick,
          },
          [
            React.createElement('input', {
              key: 'input',
              type: 'checkbox',
              className: 'remote-config-toggle__input',
              checked: isEnabled,
              readOnly: true,
            }),
            React.createElement('span', {
              key: 'slider',
              className: 'remote-config-toggle__slider',
            }),
          ],
        ),
      ),

      // Environments column
      React.createElement(
        'td',
        {
          key: 'environments',
          className: 'remote-config-table__td',
        },
        React.createElement(
          'div',
          {
            style: { display: 'flex', flexWrap: 'wrap', gap: '4px' },
          },
          environments.length > 0
            ? environments.map(function (env, index) {
                return React.createElement(
                  'span',
                  {
                    key: env + '-' + index,
                    className:
                      'remote-config-env-badge remote-config-env-badge--' + env,
                  },
                  env,
                );
              })
            : React.createElement(
                'span',
                {
                  style: { color: 'var(--color-grey-71)', fontSize: '13px' },
                },
                '-',
              ),
        ),
      ),

      // Targeting column - show all rules
      React.createElement(
        'td',
        {
          key: 'targeting',
          className: 'remote-config-table__td',
        },
        !hasTargeting
          ? React.createElement(
              'span',
              {
                style: { color: 'var(--color-grey-71)', fontSize: '13px' },
              },
              'All',
            )
          : React.createElement(
              'div',
              {
                className: 'remote-config-targeting-list',
              },
              [
                // Distributions
                distributions.length > 0 &&
                  React.createElement(
                    'div',
                    {
                      key: 'distributions',
                      className: 'remote-config-targeting-group',
                    },
                    [
                      React.createElement(
                        'span',
                        {
                          key: 'label',
                          className: 'remote-config-targeting-label',
                        },
                        'Dist:',
                      ),
                      React.createElement(
                        'div',
                        {
                          key: 'items',
                          className: 'remote-config-targeting-items',
                        },
                        distributions.map(function (rule, i) {
                          return renderRuleBadge(rule, i, 'dist');
                        }),
                      ),
                    ],
                  ),

                // Farming units
                farmingUnits.length > 0 &&
                  React.createElement(
                    'div',
                    {
                      key: 'farmingUnits',
                      className: 'remote-config-targeting-group',
                    },
                    [
                      React.createElement(
                        'span',
                        {
                          key: 'label',
                          className: 'remote-config-targeting-label',
                        },
                        'Farm:',
                      ),
                      React.createElement(
                        'div',
                        {
                          key: 'items',
                          className: 'remote-config-targeting-items',
                        },
                        farmingUnits.map(function (rule, i) {
                          return renderRuleBadge(rule, i, 'farm');
                        }),
                      ),
                    ],
                  ),

                // Users
                users.length > 0 &&
                  React.createElement(
                    'div',
                    {
                      key: 'users',
                      className: 'remote-config-targeting-group',
                    },
                    [
                      React.createElement(
                        'span',
                        {
                          key: 'label',
                          className: 'remote-config-targeting-label',
                        },
                        'Users:',
                      ),
                      React.createElement(
                        'div',
                        {
                          key: 'items',
                          className: 'remote-config-targeting-items',
                        },
                        users.map(function (email, i) {
                          return React.createElement(
                            'span',
                            {
                              key: 'user-' + i,
                              className:
                                'remote-config-rule-badge remote-config-rule-badge--user',
                              title: email,
                            },
                            email.split('@')[0],
                          );
                        }),
                      ),
                    ],
                  ),
              ],
            ),
      ),

      // Updated column
      React.createElement(
        'td',
        {
          key: 'updated',
          className: 'remote-config-table__td',
        },
        [
          React.createElement(
            'div',
            {
              key: 'time',
              style: { fontSize: '13px', color: 'var(--color-grey-45)' },
            },
            updatedTime,
          ),
          configuration.updated_by &&
            React.createElement(
              'div',
              {
                key: 'user',
                style: { fontSize: '11px', color: 'var(--color-grey-71)' },
              },
              configuration.updated_by,
            ),
        ],
      ),

      // Actions column
      React.createElement(
        'td',
        {
          key: 'actions',
          className: 'remote-config-table__td',
        },
        React.createElement(
          'div',
          {
            className: 'remote-config-actions',
          },
          [
            // Edit button
            React.createElement(
              'button',
              {
                key: 'edit',
                className: 'remote-config-btn-icon',
                onClick: handleEditClick,
                title: 'Edit configuration',
                'aria-label': 'Edit configuration',
              },
              [
                React.createElement(
                  'svg',
                  {
                    key: 'icon',
                    width: 14,
                    height: 14,
                    viewBox: '0 0 24 24',
                    fill: 'none',
                    stroke: 'currentColor',
                    strokeWidth: 2,
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                  },
                  [
                    React.createElement('path', {
                      key: 'p1',
                      d: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7',
                    }),
                    React.createElement('path', {
                      key: 'p2',
                      d: 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z',
                    }),
                  ],
                ),
                React.createElement('span', { key: 'text' }, 'Edit'),
              ],
            ),
            // Delete button
            React.createElement(
              'button',
              {
                key: 'delete',
                className:
                  'remote-config-btn-icon remote-config-btn-icon--danger',
                onClick: handleDeleteClick,
                title: 'Delete configuration',
                'aria-label': 'Delete configuration',
              },
              [
                React.createElement(
                  'svg',
                  {
                    key: 'icon',
                    width: 14,
                    height: 14,
                    viewBox: '0 0 24 24',
                    fill: 'none',
                    stroke: 'currentColor',
                    strokeWidth: 2,
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                  },
                  [
                    React.createElement('polyline', {
                      key: 'l1',
                      points: '3 6 5 6 21 6',
                    }),
                    React.createElement('path', {
                      key: 'l2',
                      d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2',
                    }),
                  ],
                ),
                React.createElement('span', { key: 'text' }, 'Delete'),
              ],
            ),
          ],
        ),
      ),
    ],
  );
};

module.exports = ConfigurationRow;
